import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAppTheme } from '../utils/themeUtils';

const ViewDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const { colors } = useAppTheme();
  
  const [document, setDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        const docData = await api.documents.getById(id);
        setDocument(docData);
        
        // Try to get content from the document
        let content = '';
        if (docData.content) {
          content = docData.content;
        } else {
          // Show a helpful message about the document system
          content = `
            <div style="padding: 30px; text-align: center; color: #666; border: 2px dashed #e0e0e0; border-radius: 12px; margin: 20px 0; background: #fafafa;">
              <div style="font-size: 48px; margin-bottom: 16px;">📄</div>
              <h3 style="color: #333; margin-bottom: 16px;">Document Successfully Saved!</h3>
              <p style="margin-bottom: 12px;"><strong>Document ID:</strong> ${docData.documentId}</p>
              <p style="margin-bottom: 12px;"><strong>Title:</strong> ${docData.title}</p>
              <p style="margin-bottom: 12px;"><strong>Type:</strong> ${docData.type || 'RICH_TEXT'}</p>
              <p style="margin-bottom: 12px;"><strong>Created:</strong> ${formatDate(docData.createdAt)}</p>
              ${docData.contentS3Key ? `<p style="margin-bottom: 16px;"><strong>Storage:</strong> Securely stored in S3</p>` : ''}
              
              <div style="background: #e3f2fd; padding: 16px; border-radius: 8px; margin: 20px 0;">
                <h4 style="color: #1976d2; margin-bottom: 8px;">🚀 Document System Status</h4>
                <p style="color: #1565c0; margin-bottom: 8px;">Your document has been successfully saved to the database and S3 storage.</p>
                <p style="color: #1565c0; margin-bottom: 0;">Content retrieval from S3 will be implemented in the next update for full document viewing.</p>
              </div>
              
              <div style="margin-top: 20px;">
                <p style="font-size: 14px; color: #888;">
                  This document is part of your <strong>PAI Doc</strong> collection and will be fully viewable once 
                  the S3 content retrieval feature is implemented.
                </p>
              </div>
            </div>
          `;
        }
        setDocumentContent(content);
      } catch (err) {
        console.error('Error fetching document:', err);
        if (err.code === 'DOCUMENT_NOT_FOUND') {
          setError('Document not found or may have been deleted.');
        } else if (err.code === 'DOCUMENT_ACCESS_DENIED') {
          setError('You do not have permission to view this document.');
        } else {
          setError('Failed to load document. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
  }, [id]);

  const handleEdit = () => {
    // Navigate to docs editor with this document loaded
    navigate(`/docs?edit=${id}`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await api.documents.delete(id);
        navigate('/documents');
      } catch (err) {
        console.error('Error deleting document:', err);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      // Handle both ISO string and epoch timestamp
      let date;
      if (typeof dateString === 'string' && dateString.includes('T')) {
        date = new Date(dateString);
      } else if (typeof dateString === 'number' || (typeof dateString === 'string' && !isNaN(dateString))) {
        // Handle epoch timestamp (convert to milliseconds if needed)
        const timestamp = Number(dateString);
        date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid and not epoch (1970)
      if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
        return 'Recently';
      }
      
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getDocumentTypeColor = (type) => {
    switch (type) {
      case 'RICH_TEXT': return theme.palette.primary.main;
      case 'MARKDOWN': return theme.palette.secondary.main;
      case 'PLAIN_TEXT': return theme.palette.info.main;
      case 'CODE': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/documents')}
          startIcon={<ArrowBackIcon />}
        >
          Back to Documents
        </Button>
      </Box>
    );
  }

  if (!document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Document not found.
        </Alert>
      </Box>
    );
  }

  const isOwner = user?.userId === document.userId;

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: colors.background,
      p: 2
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3,
            backgroundColor: colors.surface,
            borderRadius: 2
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" mb={1}>
                <IconButton 
                  onClick={() => navigate('/documents')}
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  {document.title}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip 
                  label={document.type || 'RICH_TEXT'}
                  size="small"
                  sx={{ 
                    backgroundColor: alpha(getDocumentTypeColor(document.type), 0.1),
                    color: getDocumentTypeColor(document.type),
                    fontWeight: 500
                  }}
                />
                {document.isPublic && (
                  <Chip 
                    icon={<VisibilityIcon />}
                    label="Public" 
                    size="small" 
                    color="success"
                  />
                )}
                <Chip 
                  icon={<ScheduleIcon />}
                  label={`Created ${formatDate(document.createdAt)}`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Box>

            {/* Action Buttons */}
            {isOwner && (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Edit Document">
                  <IconButton onClick={handleEdit} color="primary">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share Document">
                  <IconButton color="secondary">
                    <ShareIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton color="info">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Document">
                  <IconButton onClick={handleDelete} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Box>

          {/* Tags */}
          {document.tags && document.tags.length > 0 && (
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                Tags:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {document.tags.map((tag, index) => (
                  <Chip 
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Summary */}
          {document.summary && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                Summary:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {document.summary}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Document Content */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: 4,
            backgroundColor: colors.surface,
            borderRadius: 2,
            minHeight: '400px'
          }}
        >
          <Box 
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: colors.text,
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(1),
              },
              '& p': {
                color: colors.text,
                lineHeight: 1.6,
                marginBottom: theme.spacing(1),
              },
              '& ul, & ol': {
                color: colors.text,
                paddingLeft: theme.spacing(3),
              },
              '& blockquote': {
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                paddingLeft: theme.spacing(2),
                margin: theme.spacing(2, 0),
                fontStyle: 'italic',
                color: colors.textSecondary,
              },
              '& code': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                padding: theme.spacing(0.5, 1),
                borderRadius: 1,
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                padding: theme.spacing(2),
                borderRadius: 2,
                overflow: 'auto',
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                }
              }
            }}
            dangerouslySetInnerHTML={{ 
              __html: documentContent || '<p style="text-align: center; color: #666; padding: 40px;">No content available</p>' 
            }}
          />
        </Paper>

        {/* Document Info */}
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            mt: 2,
            backgroundColor: alpha(colors.surface, 0.5),
            borderRadius: 2
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Document ID: {document.documentId} • 
            Last updated: {formatDate(document.updatedAt)} • 
            Version: {document.version || 1}
            {document.sizeBytes && ` • Size: ${(document.sizeBytes / 1024).toFixed(1)} KB`}
          </Typography>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ViewDocument;
